/**
 * /github-activity ルートの URL search params 同期ロジック。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business URL search params から page state を復元・正規化する pure functions。default 値は URL に出さず、不正な値は黙って default にフォールバックする
 * @graph-connects none
 */

/** @graph-connects none */
export const PERIOD_DAYS_VALUES = ['7', '14', '30', '90', '180', '365'] as const;
/** @graph-connects none */
export type PeriodDays = (typeof PERIOD_DAYS_VALUES)[number];

/** @graph-connects none */
export const SCOPE_VALUES = ['all', 'team', 'member'] as const;
/** @graph-connects none */
export type Scope = (typeof SCOPE_VALUES)[number];

/**
 * ダッシュボードの集計対象メンバー。
 *
 * 以前は member-daily-report の「有効な target」から作っていたが、daily report 自体を廃止した
 * ため `useMemberList()`（engineers + qa）から derive する。GitHub login を持たないメンバーは
 * activity API を引けないので除外する。
 * @graph-connects none
 */
export interface GithubActivityMember {
  email: string;
  githubLogin: string;
  displayName?: string;
  teamName?: string;
}

/**
 * URL search 表現。default 値は URL に出さないため全 field が optional。
 * 実値の解決 (`scope ?? 'member'` 等) は consumer 側で `resolveSearch` 経由で行う。
 * @graph-connects none
 */
export interface GithubActivitySearch {
  scope?: Scope;
  team?: string;
  email?: string;
  days?: PeriodDays;
}

/** @graph-connects none */
export const GITHUB_ACTIVITY_SEARCH_DEFAULTS = {
  scope: 'member' as Scope,
  days: '90' as PeriodDays,
};

/**
 * URL search を復元する。不正な値は黙って undefined にして default fallback に乗せる。
 * 既定値と一致する値も undefined に潰して URL を最小化する。
 * @graph-connects none
 */
export function parseGithubActivitySearch(raw: Record<string, unknown>): GithubActivitySearch {
  const rawScope = raw['scope'];
  const scope =
    typeof rawScope === 'string' &&
    SCOPE_VALUES.includes(rawScope as Scope) &&
    rawScope !== GITHUB_ACTIVITY_SEARCH_DEFAULTS.scope
      ? (rawScope as Scope)
      : undefined;
  const rawDays = raw['days'];
  const days =
    typeof rawDays === 'string' &&
    PERIOD_DAYS_VALUES.includes(rawDays as PeriodDays) &&
    rawDays !== GITHUB_ACTIVITY_SEARCH_DEFAULTS.days
      ? (rawDays as PeriodDays)
      : undefined;
  const team = typeof raw['team'] === 'string' && raw['team'].length > 0 ? raw['team'] : undefined;
  const email =
    typeof raw['email'] === 'string' && raw['email'].length > 0 ? raw['email'] : undefined;
  return { scope, days, team, email };
}

/**
 * sparse search を完全形に戻して default を埋めた値を取り出す。
 * @graph-connects none
 */
export function resolveGithubActivitySearch(search: GithubActivitySearch): {
  scope: Scope;
  days: PeriodDays;
  team: string | undefined;
  email: string | undefined;
} {
  return {
    scope: search.scope ?? GITHUB_ACTIVITY_SEARCH_DEFAULTS.scope,
    days: search.days ?? GITHUB_ACTIVITY_SEARCH_DEFAULTS.days,
    team: search.team,
    email: search.email,
  };
}

/**
 * GithubActivitySearch を URL search の sparse 表現に変換する。default 値は出さない。
 * @graph-connects none
 */
export function stripGithubActivitySearchDefaults(
  search: GithubActivitySearch,
): GithubActivitySearch {
  const resolved = resolveGithubActivitySearch(search);
  const out: GithubActivitySearch = {};
  if (resolved.scope !== GITHUB_ACTIVITY_SEARCH_DEFAULTS.scope) out.scope = resolved.scope;
  if (resolved.days !== GITHUB_ACTIVITY_SEARCH_DEFAULTS.days) out.days = resolved.days;
  if (resolved.team) out.team = resolved.team;
  if (resolved.email) out.email = resolved.email;
  return out;
}

/**
 * GithubActivityParams (from/to/range) を作る。
 * @graph-connects none
 */
function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/**
 * 当該期間の `from / to` を作る。`now` は test 注入用。
 * @graph-connects none
 */
export function buildGithubActivityPeriodParams(periodDays: number, now = new Date()) {
  const to = new Date(now);
  const from = new Date(now);
  from.setDate(from.getDate() - periodDays + 1);
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

/**
 * 1 つ前の同一期間の `from / to` を作る。前期比計算用。
 * @graph-connects none
 */
export function buildGithubActivityPreviousPeriodParams(periodDays: number, now = new Date()) {
  const to = new Date(now);
  to.setDate(to.getDate() - periodDays);
  const from = new Date(to);
  from.setDate(from.getDate() - periodDays + 1);
  return { from: toIsoDate(from), to: toIsoDate(to) };
}
